import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { fetchProductById, toggleFavorite, fetchBuyProduct } from '../../api/productApi';
import { ImageSlider } from '../../components/ImageSlider/ImageSlider';
import StarRating from '../../components/StarRating/StarRating';
import { pluralizeReviews } from '../../utils/stringUtils';
import { OtherCourses } from '../../components/OtherCourses/OtherCourses';
import { NavigationTabs } from '../../components/NavigationTabs/NavigationTabs';
import { BackButton } from '../../components/BackButton/BackButton';
import { LoadingIndicator } from '../../components/LoadingIndicator/LoadingIndicator';
import { ProductVideo } from '../../types/product';
import './ProductDetails.scss';

export const ProductDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [selectedVideo, setSelectedVideo] = useState<ProductVideo | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [product, setProduct] = useState<any | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isFavorite, setIsFavorite] = useState(false);
    const [contentTab, setContentTab] = useState<'description' | 'content'>('description');
    const [navTab, setNavTab] = useState<'products' | 'favorites' | 'account'>('products');
    const [showTelegramPopup, setShowTelegramPopup] = useState(false);

    useEffect(() => {
        if (!id) return;
        const loadProduct = async () => {
            try {
                setIsLoading(true);
                const data = await fetchProductById(id);
                setProduct(data);
                setIsFavorite(data.isFavorite);
            } catch (error) {
                console.error('Ошибка загрузки товара:', error);
            } finally {
                setIsLoading(false);
            }
        };
        loadProduct();
    }, [id]);

    const handlePlayClick = (video: ProductVideo) => {
        setSelectedVideo(video);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedVideo(null);
    };

    const handleNavTabChange = (tab: 'products' | 'favorites' | 'account') => {
        setNavTab(tab);
        if (tab === 'products') navigate('/');
        else if (tab === 'favorites') navigate('/favorites');
        else if (tab === 'account') navigate('/account');
    };

    const handleFavoriteToggle = async () => {
        if (!product) return;
        try {
            await toggleFavorite(product.id);
            setIsFavorite(prev => !prev);
        } catch (error) {
            console.error('Ошибка при переключении избранного:', error);
        }
    };

    const handleBuyClick = async () => {
        if (!id) return;
        try {
            await fetchBuyProduct(id);
            navigate('/my-purchases');
        } catch (error) {
            console.error('Ошибка при покупке:', error);
        }
    };

    const handleShareClick = () => {
        setShowTelegramPopup(true);
    };

    const copyToClipboard = () => {
        const link = `https://t.me/antiage_market_bot?start=${product.id}`;
        navigator.clipboard.writeText(link)
            .then(() => alert('Ссылка скопирована!'))
            .catch(err => console.error('Ошибка копирования: ', err));
    };

    const getReviewData = () => {
        if (!product?.reviews?.length) return null;
        const review = product.reviews[0];
        let formattedDate = '';
        let shortReviewerName = '';

        try {
            if (review.date) {
                formattedDate = format(parseISO(review.date), 'd MMMM', { locale: ru });
            }
        } catch (e) {
            console.error('Ошибка форматирования даты:', e);
        }

        if (review.user) {
            const firstName = review.user.firstName || '';
            const lastName = review.user.lastName ? `${review.user.lastName.charAt(0)}.` : '';
            shortReviewerName = `${firstName} ${lastName}`.trim();
        }

        return {
            text: review.text || '',
            formattedDate,
            shortReviewerName
        };
    };

    const reviewData = getReviewData();

    if (isLoading) return <LoadingIndicator message="Загрузка товара..." />;
    if (!product || !product.images?.length) return (
        <div className="product-details__not-found">
            <BackButton />
            <div>Товар не найден</div>
        </div>
    );

    return (
        <div className="product-details">
             <BackButton className="product-details__back-button" />

            <div className="product-details__content">
                <div className="product-details__image-container">
                    <ImageSlider
                        images={product.images}
                        altText={product.title}
                        isFavorite={isFavorite}
                        onFavoriteToggle={handleFavoriteToggle}
                        onShareClick={handleShareClick}
                    />
                </div>

                <div className="product-details__info">
                    <div className="product-details__price-container">
                        {product.purchased ? (
                            <div className="product-details__purchased-badge">
                                <span className="product-details__checkmark">✓</span>
                                <span>Приобретено</span>
                            </div>
                        ) : (
                            <>
                                <div className="product-details__price-wrapper">
                                    {product.discount ? (
                                        <>
                                            <span className="product-details__current-price">
                                                {product.discount} ₽
                                            </span>
                                            <span className="product-details__old-price">
                                                {product.price} ₽
                                            </span>
                                        </>
                                    ) : (
                                        <span className="product-details__current-price">
                                            {product.price} ₽
                                        </span>
                                    )}
                                </div>
                                <span className="product-details__video-count">
                                    за {product.videosCount || 0} видео
                                </span>
                            </>
                        )}
                    </div>
                    <h1 className="product-details__title">{product.title}</h1>
                    {product.discount && !product.purchased && (
                        <div className="product-details__discount-badge">
                            <svg width="86" height="32" viewBox="0 0 86 32" fill="none">
                                <rect width="86" height="32" rx="12" fill="#FB034F" fillOpacity="0.12"/>
                                <path
                                    d="M21.68 17.704H24.256C23.328 19.864 21.232 21.208 18.8 21.208C15.344 21.208 12.576 18.472 12.576 15.048C12.576 11.704 15.312 8.968 18.656 8.968C21.168 8.968 23.232 10.296 24.256 12.568H21.808C21.008 11.528 20.08 11.08 18.704 11.08C16.464 11.08 14.784 12.792 14.784 15.064C14.784 17.288 16.576 19.096 18.752 19.096C19.888 19.096 20.704 18.712 21.68 17.704ZM25.6921 21V12.136H27.8201V16.216L30.8921 12.136H33.4681L29.9321 16.536L33.6601 21H30.8921L27.8201 16.92V21H25.6921ZM34.5515 21V12.136H37.1635V17.992L40.6315 12.136H42.7915V21H40.6795V15.144L37.2115 21H35.0515ZM43.8163 23.112V19.064H45.1763L48.1363 12.136H49.9923L52.9523 19.064H54.3123V23.112H52.7923L52.3443 21H45.8003L45.3523 23.112H43.8163ZM47.3843 19.064H50.7443L49.0643 15.064L47.3843 19.064ZM55.3328 21V12.136H57.4608V16.216L60.5328 12.136H63.1088L59.5728 16.536L63.3008 21H60.5328L57.4608 16.92V21H55.3328ZM63.7761 16.6C63.7761 13.896 65.6641 11.928 68.2881 11.928C69.5681 11.928 70.4481 12.344 71.1841 13.32V12.136H73.1361V21H71.1841V19.848C70.3361 20.824 69.5041 21.208 68.2401 21.208C65.6481 21.208 63.7761 19.272 63.7761 16.6ZM65.9201 16.648C65.9201 18.2 67.4921 19.256 69.0441 19.256C70.5801 19.256 71.1201 18.184 71.1201 16.6C71.1201 15 70.0321 13.88 68.4801 13.88C67.4921 13.88 65.9201 15.048 65.9201 16.648Z"
                                    fill="#FB034F"/>
                            </svg>
                        </div>
                    )}

                    <div className="product-details__info-blocks">
                        <div className="product-details__info-blocks-container">
                            <div className="product-details__info-block">
                                <div className="product-details__info-block-content">
                                    <div className="product-details__rating-stars">
                                        <span className="product-details__rating-star">★</span>
                                        <span className="product-details__rating-value">{product.rating}</span>
                                    </div>
                                    <div className="product-details__reviews-count">
                                        {pluralizeReviews(product.reviews?.length || 0)}
                                    </div>
                                </div>
                                <button
                                    className="product-details__info-block-button"
                                    onClick={() => navigate(`/reviews/${product.id}`)}
                                >
                                    ›
                                </button>
                            </div>
                            <div className="product-details__info-block">
                                <div className="product-details__info-block-content">
                                    <div className="product-details__author-label">
                                        Автор курса
                                    </div>
                                    <div className="product-details__author-name">
                                        {product.author || 'Не указан'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="product-details__review-block">
                            <div className="product-details__rating-container">
                                <StarRating rating={product.rating}/>
                            </div>

                            {reviewData ? (
                                <>
                                    <div className="product-details__review-author">
                                        {reviewData.shortReviewerName}
                                        {reviewData.formattedDate && (
                                            <span className="product-details__review-date">
                                                {reviewData.formattedDate}
                                            </span>
                                        )}
                                    </div>
                                    <p className="product-details__review-text">
                                        {reviewData.text}
                                    </p>
                                    <div className="product-details__review-label">
                                        один из отзывов
                                    </div>
                                </>
                            ) : (
                                <p className="product-details__review-text">
                                    Отзывов нет
                                </p>
                            )}
                        </div>

                        <div className="product-details__tabs-block">
                            <div className="product-details__tabs-header">
                                <button
                                    className={`product-details__tab-button ${contentTab === 'description' ? 'active' : ''}`}
                                    onClick={() => setContentTab('description')}
                                >
                                    Описание
                                </button>
                                <button
                                    className={`product-details__tab-button ${contentTab === 'content' ? 'active' : ''}`}
                                    onClick={() => setContentTab('content')}
                                >
                                    Состав курса
                                </button>
                            </div>

                            {contentTab === 'description' ? (
                                <div className="product-details__tab-content">
                                    <p>{product.description}</p>
                                </div>
                            ) : (
                                <div className="product-details__course-content">
                                    {product.demos?.map((video: ProductVideo, index: number) => (
                                        <React.Fragment key={video.id}>
                                            <div className="product-details__video-item">
                                                <div className="product-details__video-info">
                                                    <h4 className="product-details__video-title">{video.title}</h4>
                                                    <p className="product-details__video-description">{video.description}</p>
                                                </div>
                                                <div className="product-details__video-preview">
                                                    <img src={video.thumbnail} alt={video.title}/>
                                                    <button
                                                        className="product-details__play-button"
                                                        onClick={() => handlePlayClick(video)}
                                                    >
                                                        ▶
                                                    </button>
                                                </div>
                                            </div>
                                            {index < product.demos.length - 1 && (
                                                <div className="product-details__video-divider"/>
                                            )}
                                        </React.Fragment>
                                    ))}

                                    {isModalOpen && selectedVideo && (
                                        <div className="video-modal">
                                            <div className="video-modal__overlay" onClick={closeModal}></div>
                                            <div className="video-modal__content">
                                                <button className="video-modal__close" onClick={closeModal}>
                                                    ×
                                                </button>
                                                <div className="video-modal__player-wrapper">
                                                    <iframe
                                                        src={selectedVideo.url}
                                                        allow="autoplay; fullscreen; picture-in-picture; encrypted-media; gyroscope; accelerometer; clipboard-write; screen-wake-lock;"
                                                        frameBorder="0"
                                                        allowFullScreen
                                                        className="video-modal__iframe"
                                                    ></iframe>
                                                </div>
                                                <h3 className="video-modal__title">{selectedVideo.title}</h3>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {!product.purchased && (
                        <button
                            className="product-details__buy-button"
                            onClick={handleBuyClick}
                        >
                            Купить
                        </button>
                    )}
                    <OtherCourses
                        currentProductId={product?.id}
                    />
                </div>
            </div>

            {showTelegramPopup && (
                <div className="telegram-share-popup">
                    <div
                        className="telegram-share-popup__overlay"
                        onClick={() => setShowTelegramPopup(false)}
                    />
                    <div className="telegram-share-popup__content">
                        <button
                            className="telegram-share-popup__close"
                            onClick={() => setShowTelegramPopup(false)}
                        >
                            ×
                        </button>
                        <h3 className="telegram-share-popup__title">Поделиться в Telegram</h3>
                        <div className="telegram-share-popup__link-container">
                            <a
                                href={`https://t.me/antiage_market_bot?start=${product.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="telegram-share-popup__link"
                            >
                                {`https://t.me/antiage_market_bot?start=${product.id}`}
                            </a>
                            <button
                                className="telegram-share-popup__copy-btn"
                                onClick={copyToClipboard}
                            >
                                Копировать
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <footer className="product-details__footer">
                <NavigationTabs
                    activeTab={navTab}
                    onTabChange={handleNavTabChange}
                />
            </footer>
        </div>
    );
};